import type { LeafVariant, LeafPosition } from './Leaf';

/**
 * Seeded PRNG using Linear Congruential Generator
 * Ensures deterministic leaf positions for consistent SSR/hydration
 */
function createSeededRandom(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

/**
 * Generate leaf positions in a natural canopy pattern
 * Creates clustered, organic-looking distribution
 * Uses seeded PRNG for deterministic, consistent results
 */
export function generateLeafPositions(
  count: number,
  centerX: number,
  centerY: number,
  canopyRadius: number,
  seed: number = 42
): LeafPosition[] {
  const positions: LeafPosition[] = [];
  const random = createSeededRandom(seed);

  // Golden angle for natural spiral distribution
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));

  for (let i = 0; i < count; i++) {
    // Spiral pattern with some randomization
    const angle = i * goldenAngle;
    const radiusFactor = 0.3 + (i / count) * 0.7; // Start from center, spread out
    const radius = canopyRadius * radiusFactor * (0.8 + random() * 0.4);

    const cx = centerX + Math.cos(angle) * radius;
    const cy = centerY + Math.sin(angle) * radius * 0.7; // Flatten vertically for canopy shape

    // Vary leaf size - larger in center, smaller at edges
    const sizeFactor = 1 - (radiusFactor * 0.5);
    const size = 4 + sizeFactor * 4 + random() * 2;

    // Random rotation for natural look
    const rotation = random() * 360;

    // Color variants - more shadows at bottom, highlights at top
    let variant: LeafVariant = 'primary';
    if (cy > centerY + canopyRadius * 0.2) {
      variant = random() > 0.5 ? 'shadow' : 'primary';
    } else if (cy < centerY - canopyRadius * 0.3) {
      variant = random() > 0.6 ? 'highlight' : 'primary';
    }

    positions.push({ cx, cy, size, rotation, variant });
  }

  return positions;
}
